import { useEffect, useMemo, useRef, useState } from 'react';
import { trackBookingClick, updateUserName, getBmsSlots, bookBms } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { C } from '../utils/constants';
import { SupportModal } from './SupportModal';
import { formatClockTime } from '../utils/formatTime';

// ─── Booking Hub configuration ───────────────────────────────────────────────
// Slots and the booking itself now go through the RY backend
// (getBmsSlots / bookBms), which holds the Booking Hub API key server-side and
// derives the booking email from the caller's JWT. This component no longer
// reads VITE_RY_PORTAL_API_KEY: Vite inlines VITE_-prefixed variables into the
// built bundle, so holding that key here published it to every visitor — and it
// was the only thing guarding an endpoint that accepts an arbitrary email.
//
// BMS_FALLBACK_URL stays: it is the public booking page, shown only as an
// escape hatch when something has already gone wrong. It carries no credential.
const BOOKING_BASE_URL  = 'https://bookings.benmilston.com';
const BMS_FALLBACK_URL  = `${BOOKING_BASE_URL}/book/breakthrough-mapping-session`;

// ─── Date helpers ────────────────────────────────────────────────────────────
// All date math is done in the user's local timezone so dates on the calendar
// match what the user sees on their wall clock. The POST body sends the raw
// ISO timestamps received from the API; we only display them locally.

const TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
  catch { return 'UTC'; }
})();

const pad = (n) => String(n).padStart(2, '0');
const localDateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const sameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

const fmtTime = (d) => formatClockTime(d);
const fmtLongDate = (d) => d.toLocaleDateString(undefined, {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
});

// Monday-of-week helpers (en-GB convention — week starts Mon).
// Returns a Date at local midnight on Monday of the week containing `d`.
const mondayOf = (d) => {
  const day = d.getDay();              // 0=Sun..6=Sat
  const offset = (day + 6) % 7;        // 0=Mon..6=Sun
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - offset);
};
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

// Label like "26 – 1 Jun 2026" when same month, or "29 Dec – 4 Jan 2027"
// when straddling months. The end always shows the year; the start shows
// month only if it differs from the end month.
const weekRangeLabel = (mondayStart) => {
  const end = addDays(mondayStart, 6);
  const sameMonth = mondayStart.getMonth() === end.getMonth()
                 && mondayStart.getFullYear() === end.getFullYear();
  const startFmt = sameMonth
    ? String(mondayStart.getDate())
    : mondayStart.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const endFmt = end.toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
  return `${startFmt} – ${endFmt}`;
};

// ─── Component ───────────────────────────────────────────────────────────────

export const BookingModal = ({ onClose, daysCompleted = 0 }) => { // eslint-disable-line no-unused-vars
  const { user } = useAuth();

  // Stage machine. `error` is set independently — error stage uses it for the
  // user-facing message; non-error stages use it for inline retry context.
  const [stage, setStage] = useState('loadingSlots');
  const [error, setError] = useState('');

  // Support modal — opens the in-portal "Get in Touch" box from the footer.
  const [showSupport, setShowSupport] = useState(false);

  // 7-day BMS booking window — anchor is first_login if set, else
  // created_at (matches /api/user/can-book-bms and the Booking Hub
  // backstop). `lockFloorMs` is the earliest instant this user can book.
  //   - For new users (anchor recent): future timestamp.
  //   - For established users (anchor weeks/months old): past timestamp,
  //     making it a no-op — the calendar opens on the earliest API slot
  //     exactly as it did before.
  //   - For users with no anchor at all (defensive): null, treated as
  //     no floor.
  const BMS_BOOK_LOCK_MS = 7 * 24 * 60 * 60 * 1000;
  const lockFloorMs = useMemo(() => {
    const anchorIso = user?.firstLogin || user?.createdAt;
    return anchorIso ? new Date(anchorIso).getTime() + BMS_BOOK_LOCK_MS : null;
  }, [user?.firstLogin, user?.createdAt]);

  // Slots state — flat array of { start, end } as returned by the API.
  const [slots, setSlots] = useState([]);

  // User selection.
  const [selectedDateKey, setSelectedDateKey] = useState(null); // 'YYYY-MM-DD' local
  const [selectedSlot, setSelectedSlot] = useState(null);       // { start, end }

  // Calendar view — single-week strip. Defaults to the week containing today;
  // jumps to the week of the earliest available slot once slots are fetched.
  const today = useMemo(() => new Date(), []);
  const [viewWeekStart, setViewWeekStart] = useState(() => mondayOf(today));

  // Name handling. If the logged-in user has no first/last name on their
  // ry_users row, we let them type it on the confirmation step and save it
  // back via /api/user/name after a successful booking.
  const nameMissing = !(
    user?.firstName && user.firstName.trim() &&
    user?.lastName  && user.lastName.trim()
  );
  const [inputFirstName, setInputFirstName] = useState('');
  const [inputLastName, setInputLastName]   = useState('');
  const [nameError, setNameError]           = useState('');

  // Scroll-to-top when advancing to the confirmation step so the user always
  // sees the confirmation summary from the top, even if the calendar+times
  // step was scrolled.
  const bodyScrollRef = useRef(null);
  useEffect(() => {
    if (stage === 'confirming' && bodyScrollRef.current) {
      bodyScrollRef.current.scrollTop = 0;
    }
  }, [stage]);

  // ─── Track open (preserves existing analytics behaviour) ──────────────────
  useEffect(() => {
    trackBookingClick().catch(() => {});
  }, []);

  // ─── Fetch slots ──────────────────────────────────────────────────────────
  const fetchSlots = async () => {
    setStage('loadingSlots');
    setError('');
    try {
      // JWT-gated proxy; axios rejects on non-2xx, so the old !res.ok branch is
      // the catch below.
      const { data } = await getBmsSlots();
      const fetched = Array.isArray(data?.slots) ? data.slots : [];
      setSlots(fetched);
      if (fetched.length === 0) {
        // Empty result — show a friendly empty state in the error stage so
        // the user gets a clear "what next" rather than an empty calendar.
        setError('There are no available times right now. Please try again later, or use the link below to contact us.');
        setStage('error');
        return;
      }
      // Calendar opens on the week containing the *earliest bookable*
      // date — i.e. max(lockFloor, first API slot). For new users in
      // their first 7 days, that's anchor+7d's week. For established
      // users, lockFloor is past so this collapses to the first slot's
      // week (same as before). canPrev in SlotPicker reads from
      // slotsByDate, which we already filter to drop pre-lockFloor
      // slots — so the user is naturally prevented from paging earlier
      // than this week, no extra check needed.
      const earliestSlotMs = Math.min(...fetched.map(s => new Date(s.start).getTime()));
      const effectiveFloorMs = Math.max(lockFloorMs ?? 0, earliestSlotMs);
      const initialDate = new Date(effectiveFloorMs > 0 ? effectiveFloorMs : earliestSlotMs);
      setViewWeekStart(mondayOf(initialDate));
      setStage('pickingSlot');
    } catch (e) {
      // Prefer the backend's own message over axios's "Request failed with
      // status code N", which means nothing to a client.
      setError(
        e?.response?.data?.error ||
        e?.message ||
        'Could not load available times. Please try again.'
      );
      setStage('error');
    }
  };

  useEffect(() => {
    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Derived: slots grouped by local date key ─────────────────────────────
  // Slots before `lockFloorMs` are dropped here, so days in the visible
  // first week that fall before anchor+7d simply show no availability
  // (greyed) — same UX as any other no-availability day. The Booking
  // Hub's server-side backstop still enforces this on the POST side.
  const slotsByDate = useMemo(() => {
    const map = {};
    for (const s of slots) {
      if (lockFloorMs !== null && new Date(s.start).getTime() < lockFloorMs) continue;
      const key = localDateKey(new Date(s.start));
      if (!map[key]) map[key] = [];
      map[key].push(s);
    }
    // Sort time-chips within each date
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => new Date(a.start) - new Date(b.start));
    }
    return map;
  }, [slots, lockFloorMs]);

  // ─── Submit booking ───────────────────────────────────────────────────────
  const submitBooking = async () => {
    if (!selectedSlot || !user) return;

    // Resolve which name to use. If the user's row already had a name, we
    // always use that (no input was even rendered). Otherwise we use what
    // they typed on the confirmation step, validated here as a last guard.
    const firstToSend = nameMissing ? inputFirstName.trim() : (user.firstName || '').trim();
    const lastToSend  = nameMissing ? inputLastName.trim()  : (user.lastName  || '').trim();
    if (!firstToSend || !lastToSend) {
      setNameError('Please enter your first and last name.');
      return;
    }
    setNameError('');

    setStage('submitting');
    setError('');
    try {
      // No email and no session_type_id are sent: the backend fixes the session
      // type and reads the address from the caller's own ry_users row. That is
      // what stops anyone booking as somebody else — the browser has no say in
      // whose booking this is.
      await bookBms({
        start_time: selectedSlot.start,
        end_time: selectedSlot.end,
        first_name: firstToSend,
        last_name: lastToSend,
        client_timezone: TZ,
      });

      // Booking succeeded. If the user had to type their name (because their
      // ry_users row had no first/last name), persist it back so they're
      // never asked again. This is fire-and-forget — a save failure must
      // never invalidate a successful booking.
      if (nameMissing) {
        updateUserName(firstToSend, lastToSend).catch((e) => {
          // eslint-disable-next-line no-console
          console.warn('[BookingModal] name save failed (booking still succeeded):', e?.message || e);
        });
      }

      setStage('success');
    } catch (e) {
      // axios rejects on any non-2xx, so every branch that used to read
      // res.status now reads e.response.status. The backend passes Booking
      // Hub's status and JSON body through untouched, so the statuses and
      // reason codes below are exactly the ones this component always handled.
      const status = e?.response?.status;
      const body   = e?.response?.data;

      if (status === 409) {
        // Slot taken between fetch and submit. Re-fetch and bounce the user
        // back to pick another time, with an inline note.
        setError('That time was just taken by someone else — please choose another time.');
        setSelectedSlot(null);
        await fetchSlots();
        return;
      }

      // Booking Hub's BMS window backstop — there are two distinct 403s:
      //   bms_lock_active     → date is BEFORE the user's anchor+7d floor
      //   bms_window_exceeded → date is AFTER today + max_days_ahead
      // These are edge cases (the calendar already filters out both ends
      // of the window) but a stale frontend or a determined direct POST
      // can still hit them. Bounce back to the picker with an inline
      // notice rather than the technical-error stage — these aren't
      // technical failures.
      if (status === 403 &&
          (body?.reason === 'bms_lock_active' || body?.reason === 'bms_window_exceeded')) {
        const fallback = body?.reason === 'bms_lock_active'
          ? "That date isn't bookable yet — bookings open 7 days after your first login. Please pick a later date."
          : 'That date is too far ahead — please pick a date within the bookable window.';
        setError(body?.error || fallback);
        setSelectedSlot(null);
        setStage('pickingSlot');
        return;
      }

      // Everything else — any other 403, any other non-2xx, or a network
      // failure — lands on the technical-error stage as it did before.
      const detail = body?.error || body?.message || '';
      setError(
        detail ||
        (status
          ? `Something went wrong booking your session (HTTP ${status}). Please try again, or use the link below to book directly.`
          : 'Network error — please check your connection and try again.')
      );
      setStage('error');
    }
  };

  // ─── Render helpers ───────────────────────────────────────────────────────
  const TryAgainButton = ({ label = 'Try again', onClick }) => (
    <button onClick={onClick} style={primaryBtn}>{label}</button>
  );

  const FallbackLink = () => (
    <a
      href={BMS_FALLBACK_URL}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-block',
        marginTop: 12,
        color: C.terracotta,
        fontSize: '0.85rem',
        textDecoration: 'underline',
      }}
    >
      Or open the booking page in a new tab →
    </a>
  );

  // ─── Stage renders ────────────────────────────────────────────────────────
  let body;

  if (stage === 'loadingSlots') {
    body = (
      <CenterMessage>
        <Spinner />
        <p style={loadingText}>Loading available times…</p>
      </CenterMessage>
    );
  } else if (stage === 'error') {
    body = (
      <CenterMessage>
        <div style={errorIcon}>⚠️</div>
        <p style={{ ...errorText, marginBottom: 16 }}>{error}</p>
        <TryAgainButton onClick={fetchSlots} />
        <FallbackLink />
      </CenterMessage>
    );
  } else if (stage === 'pickingSlot') {
    body = (
      <SlotPicker
        viewWeekStart={viewWeekStart}
        setViewWeekStart={setViewWeekStart}
        slotsByDate={slotsByDate}
        selectedDateKey={selectedDateKey}
        setSelectedDateKey={setSelectedDateKey}
        setSelectedSlot={setSelectedSlot}
        selectedSlot={selectedSlot}
        onContinue={() => setStage('confirming')}
        inlineNotice={error}
        today={today}
      />
    );
  } else if (stage === 'confirming') {
    body = (
      <Confirmation
        slot={selectedSlot}
        user={user}
        nameMissing={nameMissing}
        inputFirstName={inputFirstName}
        setInputFirstName={setInputFirstName}
        inputLastName={inputLastName}
        setInputLastName={setInputLastName}
        nameError={nameError}
        onBack={() => setStage('pickingSlot')}
        onConfirm={submitBooking}
      />
    );
  } else if (stage === 'submitting') {
    body = (
      <CenterMessage>
        <Spinner />
        <p style={loadingText}>Booking your session…</p>
      </CenterMessage>
    );
  } else if (stage === 'success') {
    const start = new Date(selectedSlot.start);
    body = (
      <CenterMessage>
        <div style={{ fontSize: '3rem', marginBottom: 8 }}>✓</div>
        <h3 style={successHeading}>Your session is booked</h3>
        <p style={{ ...loadingText, marginBottom: 4 }}>{fmtLongDate(start)}</p>
        <p style={{ ...loadingText, marginBottom: 20 }}>at {fmtTime(start)}</p>
        <p style={{ ...subtleText, marginBottom: 20 }}>
          {user?.email
            ? `We've emailed your booking confirmation to ${user.email}.`
            : "We've emailed your booking confirmation to you."}
        </p>
        <button onClick={onClose} style={primaryBtn}>Got it</button>
      </CenterMessage>
    );
  }

  // ─── Outer chrome — unchanged from the original modal ─────────────────────
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={{
        background: '#fff',
        borderRadius: 16,
        overflow: 'hidden',
        width: '100%',
        maxWidth: 680,
        height: '95vh',
        maxHeight: '95vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        animation: 'fadeInUp 0.3s ease both',
      }}>
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${C.darkBrown} 0%, ${C.brown} 100%)`,
          padding: '24px 28px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexShrink: 0,
        }}>
          <div>
            <div style={{
              fontFamily: "'Forum', serif",
              fontSize: '0.72rem',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: C.gold,
              marginBottom: 6,
            }}>
              🎯 YOUR 1:1 SESSION
            </div>
            <h2 style={{
              fontFamily: "'Forum', serif",
              color: '#fff',
              fontSize: '1.5rem',
              letterSpacing: '0.03em',
              marginBottom: 6,
            }}>
              Breakthrough Mapping Session
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem' }}>
              With Ben · 90 minutes · Via Zoom
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: '#fff',
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '1.1rem',
              flexShrink: 0,
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div ref={bodyScrollRef} style={{ flex: 1, overflow: 'auto', minHeight: 0, background: '#fff' }}>
          {body}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 28px',
          borderTop: `1px solid ${C.border}`,
          background: C.veryLightBrown,
          textAlign: 'center',
          fontSize: '0.82rem',
          color: C.terracotta,
          flexShrink: 0,
        }}>
          Can't find a time?{' '}
          <button
            onClick={() => setShowSupport(true)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: C.brown,
              fontWeight: 600,
              fontSize: '0.82rem',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontFamily: 'inherit',
            }}
          >
            Send me a message
          </button>
        </div>
      </div>

      {/* In-portal contact box — opens above the booking modal overlay.
          SupportModal is position:fixed; inset:0 so it stacks on top
          naturally. We raise zIndex to 1100 (vs booking modal's 1000) via
          a wrapper so it is guaranteed to sit above the booking overlay
          without modifying SupportModal itself. */}
      {showSupport && (
        <div style={{ position: 'relative', zIndex: 1100 }}>
          <SupportModal onClose={() => setShowSupport(false)} />
        </div>
      )}
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const CenterMessage = ({ children }) => (
  <div style={{
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 28px',
    textAlign: 'center',
  }}>
    {children}
  </div>
);

const Spinner = () => (
  <div
    style={{
      width: 36,
      height: 36,
      border: `3px solid ${C.veryLightBrown}`,
      borderTopColor: C.gold,
      borderRadius: '50%',
      animation: 'rySpin 0.9s linear infinite',
      marginBottom: 16,
    }}
  >
    {/* The keyframes need to live somewhere — inject once. */}
    <style>{`@keyframes rySpin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const SlotPicker = ({
  // viewWeekStart / setViewWeekStart are kept in the props signature so the
  // parent call site is unchanged, but the new design uses pageIndex instead.
  slotsByDate,
  selectedDateKey, setSelectedDateKey,
  selectedSlot, setSelectedSlot,
  onContinue,
  inlineNotice,
}) => {
  // ── Available-days paging ─────────────────────────────────────────────────
  // dateKeys is already lock-filtered (slotsByDate drops pre-lockFloor slots).
  // Sorting gives ascending chronological order; page 0 always starts at the
  // soonest available date, matching the prior "jump to earliest" behaviour.
  const dateKeys = Object.keys(slotsByDate).sort();
  const PAGE_SIZE = 7;
  const [pageIndex, setPageIndex] = useState(0);

  // Reset to page 0 whenever the underlying slot data changes (e.g. 409 re-fetch
  // calls fetchSlots again, which resets `slots` → recomputes slotsByDate).
  useEffect(() => {
    setPageIndex(0);
  }, [slotsByDate]);

  const totalPages = Math.max(1, Math.ceil(dateKeys.length / PAGE_SIZE));
  const canPrev    = pageIndex > 0;
  const canNext    = pageIndex < totalPages - 1;
  const pageSlice  = dateKeys.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE);

  const timesForSelected = selectedDateKey ? slotsByDate[selectedDateKey] || [] : [];

  // Scroll the notice into view when it appears so a failed-confirm bounce
  // isn't silent.
  const noticeRef = useRef(null);
  useEffect(() => {
    if (inlineNotice && noticeRef.current) {
      noticeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [inlineNotice]);

  return (
    <div style={{ padding: '24px 28px' }}>
      {inlineNotice && (
        <div
          ref={noticeRef}
          role="alert"
          style={{
            background: 'rgba(184,52,24,0.08)',
            border: `1px solid rgba(184,52,24,0.4)`,
            borderLeft: '4px solid #B83418',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 18,
            color: '#B83418',
            fontSize: '0.95rem',
            lineHeight: 1.5,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <span aria-hidden="true" style={{ flexShrink: 0, fontSize: '1.2rem', lineHeight: 1.25 }}>⚠</span>
          <span style={{ flex: 1 }}>{inlineNotice}</span>
        </div>
      )}

      {/* Page nav — arrows only, no date-range header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button
          onClick={() => canPrev && setPageIndex(p => p - 1)}
          disabled={!canPrev}
          style={navBtn(canPrev)}
          aria-label="Previous dates"
        >
          ‹
        </button>
        <button
          onClick={() => canNext && setPageIndex(p => p + 1)}
          disabled={!canNext}
          style={navBtn(canNext)}
          aria-label="Next dates"
        >
          ›
        </button>
      </div>

      {/* Available day chips — one per available date, up to 7 per page.
          Every chip shown is guaranteed available (slotsByDate is pre-filtered),
          so there are no disabled/greyed cells. */}
      <div style={availDayGrid}>
        {pageSlice.map((key) => {
          // Parse 'YYYY-MM-DD' key at local noon to avoid UTC-offset date shift
          // (new Date('YYYY-MM-DD') is UTC midnight which drifts in ±UTC zones).
          const d = new Date(key + 'T12:00:00');
          const isSelected = selectedDateKey === key;
          return (
            <button
              key={key}
              onClick={() => {
                setSelectedDateKey(key);
                setSelectedSlot(null);
              }}
              style={availDayChip(isSelected)}
              aria-label={fmtLongDate(d)}
            >
              <span style={{ ...chipWeekday, color: isSelected ? '#fff' : C.brown }}>
                {d.toLocaleDateString(undefined, { weekday: 'short' })}
              </span>
              <span style={{ ...chipDate, color: isSelected ? 'rgba(255,255,255,0.85)' : C.terracotta }}>
                {d.toLocaleDateString(undefined, { month: 'short' })} {d.getDate()}
              </span>
              {!isSelected && <span style={dot(true)} />}
            </button>
          );
        })}
      </div>

      {/* Times — unchanged */}
      <div style={{ marginTop: 22 }}>
        {!selectedDateKey ? (
          <p style={{ ...subtleText, textAlign: 'center', marginTop: 4 }}>
            Pick a date
          </p>
        ) : timesForSelected.length === 0 ? (
          <p style={{ ...subtleText, textAlign: 'center', marginTop: 4 }}>
            No times available for this date.
          </p>
        ) : (
          <>
            <div style={{
              fontFamily: "'Forum', serif",
              color: C.brown,
              fontSize: '0.95rem',
              marginBottom: 10,
              letterSpacing: '0.04em',
            }}>
              {fmtLongDate(new Date(selectedDateKey))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {timesForSelected.map(s => {
                const isPicked = selectedSlot?.start === s.start;
                return (
                  <button
                    key={s.start}
                    onClick={() => setSelectedSlot(s)}
                    style={timeChip(isPicked)}
                  >
                    {fmtTime(new Date(s.start))}
                  </button>
                );
              })}
            </div>
            <p style={{ ...subtleText, marginTop: 12, fontSize: '0.78rem' }}>
              Times shown in your timezone ({TZ}).
            </p>
          </>
        )}
      </div>

      {/* Continue — unchanged */}
      {selectedSlot && (
        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <button onClick={onContinue} style={primaryBtn}>
            Continue →
          </button>
        </div>
      )}
    </div>
  );
};

const Confirmation = ({
  slot, user,
  nameMissing,
  inputFirstName, setInputFirstName,
  inputLastName, setInputLastName,
  nameError,
  onBack, onConfirm,
}) => {
  const start = new Date(slot.start);
  const end = new Date(slot.end);
  return (
    <div style={{ padding: '28px' }}>
      <h3 style={{
        fontFamily: "'Forum', serif",
        color: C.brown,
        fontSize: '1.3rem',
        letterSpacing: '0.03em',
        marginBottom: 6,
      }}>
        Confirm Your Booking
      </h3>
      <p style={{ ...subtleText, marginBottom: 20 }}>
        Please check the details below, then confirm.
      </p>

      <div style={{
        background: C.veryLightBrown,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '18px 20px',
        marginBottom: 20,
      }}>
        <Row label="Session"  value="Breakthrough Mapping Session" />
        <Row label="Date"     value={fmtLongDate(start)} />
        <Row label="Time"     value={`${fmtTime(start)} – ${fmtTime(end)}`} />
        <Row label="Duration" value="90 minutes" />
        <Row label="Format"   value="Via Zoom" />
      </div>

      <div style={{
        background: '#fff',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '18px 20px',
        marginBottom: 24,
      }}>
        <div style={{
          fontFamily: "'Forum', serif",
          fontSize: '0.7rem',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: C.terracotta,
          marginBottom: 8,
        }}>
          Booking under
        </div>

        {nameMissing ? (
          <>
            {/* Editable name fields — shown only when the user's ry_users
                row has no first/last name. Saved back after a successful
                booking via /api/user/name. */}
            <div style={inputGroupRow}>
              <div style={{ flex: 1 }}>
                <label style={inputLabel}>First Name</label>
                <input
                  type="text"
                  value={inputFirstName}
                  onChange={(e) => setInputFirstName(e.target.value)}
                  placeholder="First name"
                  autoComplete="given-name"
                  required
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={inputLabel}>Last Name</label>
                <input
                  type="text"
                  value={inputLastName}
                  onChange={(e) => setInputLastName(e.target.value)}
                  placeholder="Last name"
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>
            {nameError && (
              <div style={{
                color: C.terracotta,
                fontSize: '0.82rem',
                marginTop: 4,
                marginBottom: 6,
              }}>
                {nameError}
              </div>
            )}
            <Row label="Email" value={user?.email || '—'} last />
          </>
        ) : (
          <>
            <Row
              label="Name"
              value={`${user.firstName} ${user.lastName}`.trim()}
            />
            <Row label="Email" value={user?.email || '—'} last />
          </>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <button onClick={onBack} style={secondaryBtn}>← Back</button>
        <button onClick={onConfirm} style={primaryBtn}>Confirm Booking</button>
      </div>
    </div>
  );
};

const Row = ({ label, value, last = false }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 16,
    padding: '6px 0',
    borderBottom: last ? 'none' : `1px solid ${C.border}`,
    fontSize: '0.92rem',
  }}>
    <span style={{ color: C.terracotta, fontSize: '0.82rem', letterSpacing: '0.04em' }}>
      {label}
    </span>
    <span style={{
      color: C.brown,
      fontFamily: "'Forum', serif",
      letterSpacing: '0.02em',
      textAlign: 'right',
    }}>
      {value}
    </span>
  </div>
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const primaryBtn = {
  fontFamily: "'Forum', serif",
  background: `linear-gradient(135deg, ${C.amber}, ${C.gold})`,
  border: 'none',
  color: C.darkBrown,
  padding: '12px 24px',
  borderRadius: 8,
  fontSize: '0.95rem',
  fontWeight: 700,
  cursor: 'pointer',
  letterSpacing: '0.03em',
  boxShadow: '0 3px 12px rgba(245,166,35,0.35)',
};

const secondaryBtn = {
  fontFamily: "'Forum', serif",
  background: '#fff',
  border: `1.5px solid ${C.border}`,
  color: C.brown,
  padding: '12px 22px',
  borderRadius: 8,
  fontSize: '0.95rem',
  fontWeight: 600,
  cursor: 'pointer',
  letterSpacing: '0.03em',
};

const navBtn = (enabled) => ({
  background: enabled ? C.veryLightBrown : 'transparent',
  border: `1px solid ${enabled ? C.border : 'transparent'}`,
  color: enabled ? C.brown : 'rgba(107,62,10,0.25)',
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: enabled ? 'pointer' : 'default',
  fontSize: '1.2rem',
  fontFamily: "'Forum', serif",
});

const weekdayRow = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 4,
  marginBottom: 6,
};

const weekdayCell = {
  textAlign: 'center',
  fontSize: '0.7rem',
  letterSpacing: '0.1em',
  color: C.terracotta,
  textTransform: 'uppercase',
  paddingBottom: 4,
};

const dayGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 4,
};

const dayCell = (enabled, selected, isToday) => ({
  position: 'relative',
  aspectRatio: '1 / 1',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: selected ? `linear-gradient(135deg, ${C.amber}, ${C.gold})` : '#fff',
  color: selected ? '#fff' : enabled ? C.brown : 'rgba(107,62,10,0.3)',
  border: `1.5px solid ${selected ? C.gold : isToday ? C.terracotta : C.border}`,
  borderRadius: 8,
  fontFamily: "'Forum', serif",
  fontSize: '0.95rem',
  cursor: enabled ? 'pointer' : 'default',
  opacity: enabled ? 1 : 0.45,
  padding: 0,
  transition: 'background 0.15s, border-color 0.15s',
});

const dot = (enabled) => ({
  position: 'absolute',
  bottom: 4,
  width: 5,
  height: 5,
  borderRadius: '50%',
  background: enabled ? C.gold : 'transparent',
});

// ── Available-day chip styles (new date picker) ───────────────────────────

const availDayGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 6,
  marginBottom: 4,
};

// Each chip: vertical flex card, amber/gold gradient when selected — mirrors
// the visual language of the old dayCell and the time chips.
const availDayChip = (selected) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 2,
  padding: '10px 2px 16px',
  background: selected ? `linear-gradient(135deg, ${C.amber}, ${C.gold})` : '#fff',
  border: `1.5px solid ${selected ? C.gold : C.border}`,
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'background 0.15s, border-color 0.15s',
  boxShadow: selected ? '0 2px 8px rgba(245,166,35,0.35)' : 'none',
});

// Weekday abbreviation (e.g. "Thu") — the prominent top line of each chip.
// Bold, larger, in the strong palette so the day-of-week reads at a glance.
const chipWeekday = {
  fontFamily: "'Forum', serif",
  fontSize: '0.98rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  lineHeight: 1.15,
};

// Date line (month abbrev + day number, e.g. "Jun 8") — smaller, lighter
// text beneath the weekday so the hierarchy is weekday → date.
const chipDate = {
  fontSize: '0.62rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  lineHeight: 1.2,
};

const timeChip = (selected) => ({
  fontFamily: "'Forum', serif",
  background: selected ? `linear-gradient(135deg, ${C.amber}, ${C.gold})` : '#fff',
  border: `1.5px solid ${selected ? C.gold : C.border}`,
  color: selected ? '#fff' : C.brown,
  padding: '8px 14px',
  borderRadius: 8,
  fontSize: '0.92rem',
  cursor: 'pointer',
  letterSpacing: '0.02em',
  minWidth: 78,
  textAlign: 'center',
  boxShadow: selected ? '0 2px 8px rgba(245,166,35,0.35)' : 'none',
});

const loadingText = {
  color: C.brown,
  fontSize: '0.95rem',
  fontFamily: "'Forum', serif",
  letterSpacing: '0.03em',
};

const subtleText = {
  color: C.terracotta,
  fontSize: '0.85rem',
};

const successHeading = {
  fontFamily: "'Forum', serif",
  color: C.brown,
  fontSize: '1.4rem',
  letterSpacing: '0.03em',
  marginBottom: 10,
};

const errorIcon = {
  fontSize: '2.2rem',
  marginBottom: 8,
};

const errorText = {
  color: C.brown,
  fontSize: '0.95rem',
  lineHeight: 1.5,
  maxWidth: 420,
};

const inputGroupRow = {
  display: 'flex',
  gap: 12,
  marginBottom: 12,
  flexWrap: 'wrap',
};

const inputLabel = {
  display: 'block',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: C.brown,
  marginBottom: 6,
  letterSpacing: '0.04em',
};
